import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AccountBadge } from "@/components/account-badge";
import { ImpactStatusPill } from "@/components/impact-status-pill";
import { ImpactProjectDialog } from "@/components/impact-project-dialog";
import { MilestoneVerification } from "@/components/milestone-verification";
import { VideoPlayer } from "@/components/video-player";
import { MediaReviewBadge, MediaReviewPlaceholder } from "@/components/media-review-badge";
import { canWatch, normalizeReviewState, useIsMediaReviewer } from "@/lib/media-review";
import { MediaAttachments } from "@/components/media-attachments";
import { MediaLightbox } from "@/components/media-lightbox";
import { useMediaUploads } from "@/lib/use-media-uploads";
import { VIDEO_ACCEPT, MEDIA_LIMITS, formatDuration } from "@/lib/media";
import { CATEGORIES, type Category } from "@/lib/categories";
import { STATUS_META } from "@/lib/reports";
import { optimizedImage } from "@/lib/image-url";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  IMPACT_STATUS_META,
  OFFICIAL_SETTABLE_STATUSES,
  addProjectFeedback,
  addProjectMedia,
  addProjectSource,
  addProjectUpdate,
  fetchImpactProject,
  fetchLinkedReports,
  fetchMyVerification,
  fetchProjectFeedback,
  fetchProjectMedia,
  fetchProjectMetrics,
  fetchProjectSources,
  fetchProjectUpdates,
  formatDate,
  linkReport,
  projectTimeline,
  removeProjectMedia,
  searchLinkableReports,
  setMyVerification,
  unlinkReport,
  uploadImpactFile,
  type ImpactFeedback,
  type ImpactMedia,
  type ImpactProject,
  type ImpactProjectMetrics,
  type ImpactSource,
  type ImpactStatus,
  type ImpactUpdate,
  type LinkedReport,
} from "@/lib/impact-projects";
import {
  Building2, MapPin, ExternalLink, Loader2, Pencil, Plus, FileText, CheckCircle2,
  MessageSquare, ThumbsUp, ThumbsDown, Paperclip, Trash2, Link2, ArrowLeft,
} from "lucide-react";

export const Route = createFileRoute("/impact/projects/$id")({
  component: ProjectDetailPage,
  head: () => ({
    meta: [
      { title: "Project record — Xamseen" },
      { name: "description", content: "A public project record on Xamseen: what was promised, what has been done, and what residents say about the result." },
      { property: "og:title", content: "Project record — Xamseen" },
      { property: "og:description", content: "The full timeline of a public project: the original problem, the announcement, the work, completion and community verification." },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
});

function ProjectDetailPage() {
  const { id } = Route.useParams();
  const { user } = useAuth();
  const isReviewer = useIsMediaReviewer();
  const navigate = useNavigate();

  const [project, setProject] = useState<ImpactProject | null>(null);
  const [metrics, setMetrics] = useState<ImpactProjectMetrics | null>(null);
  const [updates, setUpdates] = useState<ImpactUpdate[]>([]);
  const [media, setMedia] = useState<ImpactMedia[]>([]);
  const [photoIdx, setPhotoIdx] = useState<number | null>(null);
  const [sources, setSources] = useState<ImpactSource[]>([]);
  const [reports, setReports] = useState<LinkedReport[]>([]);
  const [feedback, setFeedback] = useState<ImpactFeedback[]>([]);
  const [myStance, setMyStance] = useState<"confirmed" | "disputed" | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    const p = await fetchImpactProject(id);
    setProject(p);
    if (!p) { setLoading(false); return; }
    const [m, u, md, s, r, f] = await Promise.all([
      fetchProjectMetrics(id).catch(() => null),
      fetchProjectUpdates(id),
      fetchProjectMedia(id),
      fetchProjectSources(id),
      fetchLinkedReports(id),
      fetchProjectFeedback(id),
    ]);
    setMetrics(m);
    setUpdates(u);
    setMedia(md);
    setSources(s);
    setReports(r);
    setFeedback(f);
    setLoading(false);
  }, [id]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!user) { setMyStance(null); return; }
    fetchMyVerification(id, user.id).then((v) => setMyStance(v?.stance ?? null));
  }, [id, user]);

  if (loading) {
    return <AppShell allowAnonymous><div className="flex justify-center py-20"><Loader2 className="h-5 w-5 animate-spin" /></div></AppShell>;
  }

  if (!project) {
    return (
      <AppShell allowAnonymous>
        <div className="panel px-6 py-16 text-center">
          <h1 className="font-display text-xl font-bold">This project record is not available</h1>
          <p className="mt-2 text-sm text-muted-foreground">It may have been withdrawn by the office that published it.</p>
          <Link to="/impact/projects" className="mt-5 inline-flex rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground">
            All projects
          </Link>
        </div>
      </AppShell>
    );
  }

  const isOwner = !!user && user.id === project.owner_id;
  const cat = CATEGORIES[(project.category as Category) ?? "infrastructure"];
  const place = [project.location_name, project.city, project.country].filter(Boolean).join(", ");
  const owner = project.owner;
  const ownerName = owner?.full_name || owner?.username || "Verified office";

  async function vote(stance: "confirmed" | "disputed") {
    if (!user) { void navigate({ to: "/signin", search: { redirect: `/impact/projects/${id}` } as any }); return; }
    try {
      const next = myStance === stance ? null : stance;
      await setMyVerification(id, user.id, next);
      setMyStance(next);
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? "That could not be recorded.");
    }
  }

  return (
    <AppShell
      allowAnonymous
      rightRail={
        <div className="space-y-4 py-3">
          <div className="panel px-4 py-4">
            <h3 className="font-display text-[15px] font-semibold">Counted, not claimed</h3>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Every figure on this page is counted by Xamseen from records on the platform. The office publishing this
              project cannot type in its own numbers.
            </p>
          </div>
          {sources.length > 0 || project.source_url ? (
            <div className="panel px-4 py-4">
              <h3 className="font-display text-[15px] font-semibold">Sources</h3>
              <ul className="mt-2 space-y-2 text-sm">
                {project.source_url ? <SourceLink label="Official project page" url={project.source_url} /> : null}
                {sources.map((s) => <SourceLink key={s.id} label={s.label} url={s.url} publisher={s.publisher} />)}
              </ul>
            </div>
          ) : null}
        </div>
      }
    >
      <article className="space-y-6">
        <Link to="/impact/projects" className="inline-flex items-center gap-1.5 text-[13px] font-medium text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> All projects
        </Link>

        {project.cover_url ? (
          <img
            src={optimizedImage(project.cover_url, 1400)}
            alt=""
            className="h-56 w-full rounded-2xl object-cover sm:h-72"
          />
        ) : null}

        <header>
          <div className="flex flex-wrap items-center gap-2">
            <ImpactStatusPill status={project.status} size="md" />
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <cat.icon className="h-4 w-4" style={{ color: cat.color }} /> {cat.label}
            </span>
          </div>
          <h1 className="mt-3 font-display text-[clamp(1.5rem,5vw,2.25rem)] font-black leading-[1.1] tracking-[-0.02em]">
            {project.title}
          </h1>
          {project.summary ? <p className="mt-2.5 text-[15px] leading-relaxed text-muted-foreground">{project.summary}</p> : null}

          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-muted-foreground">
            {project.organization ? (
              <span className="inline-flex items-center gap-1.5"><Building2 className="h-4 w-4" />{project.organization}</span>
            ) : null}
            {place ? <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" />{place}</span> : null}
            {project.source_url ? (
              <a href={project.source_url} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1.5 font-medium text-foreground hover:underline">
                <ExternalLink className="h-4 w-4" /> Official source
              </a>
            ) : null}
          </div>

          {owner ? (
            <div className="panel mt-4 flex items-center gap-3 px-4 py-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={owner.avatar_url ?? undefined} />
                <AvatarFallback>{ownerName[0]}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <Link to="/profile/$username" params={{ username: owner.username }} className="truncate text-sm font-semibold hover:underline">
                    {ownerName}
                  </Link>
                  <AccountBadge accountType={owner.account_type as any} verified={owner.verified} size="sm" />
                </div>
                <p className="truncate text-xs text-muted-foreground">
                  {owner.office ? `${owner.office} · ` : ""}Published this record
                </p>
              </div>
              {isOwner ? (
                <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                  <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
                </Button>
              ) : null}
            </div>
          ) : null}
        </header>

        {metrics ? (
          <section className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat value={metrics.linked_reports} label="Reports linked" />
            <Stat value={metrics.resolved_reports} label="Reports resolved" />
            <Stat value={metrics.updates} label="Official updates" />
            <Stat value={metrics.confirmations} label="Residents confirmed" />
          </section>
        ) : null}

        {project.description ? (
          <section className="panel px-5 py-5">
            <h2 className="font-display text-[15px] font-semibold">What this covers</h2>
            <p className="mt-2 whitespace-pre-line text-[15px] leading-relaxed text-muted-foreground">{project.description}</p>
          </section>
        ) : null}

        {/* Timeline */}
        <section className="panel px-5 py-5">
          <h2 className="font-display text-[15px] font-semibold">How it progressed</h2>
          <p className="text-xs text-muted-foreground">From the problem that prompted it to the community's verdict.</p>
          <ol className="mt-4 space-y-0">
            {projectTimeline(project).map((step, i, arr) => {
              const meta = IMPACT_STATUS_META[step.key];
              const stepSources = updates
                .filter((u) => u.phase === step.key && u.source_url)
                .map((u) => ({ id: u.id, url: u.source_url as string, label: `Official record · ${formatDate(u.created_at) ?? ""}`.trim() }));
              const isFirst = i === 0;
              return (
                <li key={step.key} className="flex gap-3.5">
                  <div className="flex flex-col items-center">
                    <span
                      className={cn("mt-1 h-3 w-3 shrink-0 rounded-full border-2", step.reached ? "" : "border-border bg-background")}
                      style={step.reached ? { backgroundColor: meta.color, borderColor: meta.color } : undefined}
                    />
                    {i < arr.length - 1 ? (
                      <span className={cn("w-px flex-1", step.reached ? "bg-border" : "bg-border/50")} />
                    ) : null}
                  </div>
                  <div className={cn("min-w-0 pb-5", step.reached ? "" : "opacity-55")}>
                    <p className="text-sm font-semibold">{step.label}</p>
                    <p className="text-xs text-muted-foreground">
                      {step.date ? formatDate(step.date) : step.reached ? meta.note : "Not yet"}
                    </p>
                    {isFirst && project.source_url ? (
                      <TimelineSource url={project.source_url} label="Official project page" />
                    ) : null}
                    {stepSources.map((s) => (
                      <TimelineSource key={s.id} url={s.url} label={s.label} />
                    ))}
                  </div>
                </li>
              );
            })}
          </ol>

          {sources.length > 0 || project.source_url ? (
            <div className="mt-1 border-t border-border pt-4">
              <h3 className="text-[13px] font-semibold">Official sources</h3>
              <p className="text-xs text-muted-foreground">Published records this project is based on.</p>
              <ul className="mt-2.5 space-y-2 text-sm">
                {project.source_url ? <SourceLink label="Official project page" url={project.source_url} /> : null}
                {sources.map((s) => <SourceLink key={s.id} label={s.label} url={s.url} publisher={s.publisher} />)}
              </ul>
            </div>
          ) : null}
        </section>

        {/* Milestones and their community verification */}
        <MilestoneVerification projectId={id} isOwner={isOwner} userId={user?.id ?? null} />

        {/* Updates */}
        <section className="panel px-5 py-5">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-display text-[15px] font-semibold">Progress updates</h2>
            {isOwner ? <span className="text-xs text-muted-foreground">{updates.length} posted</span> : null}
          </div>
          {updates.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">No updates posted yet.</p>
          ) : (
            <ul className="mt-4 space-y-4">
              {updates.map((u) => (
                <li key={u.id} className="border-l-2 border-border pl-4">
                  <div className="flex flex-wrap items-center gap-2">
                    {u.phase ? <ImpactStatusPill status={u.phase} /> : null}
                    <span className="text-xs text-muted-foreground">{formatDate(u.created_at)}</span>
                  </div>
                  <p className="mt-1.5 whitespace-pre-line text-sm leading-relaxed">{u.body}</p>
                  {u.image_url ? (
                    <img src={optimizedImage(u.image_url, 900)} alt="" className="mt-2.5 max-h-72 w-full rounded-xl object-cover" />
                  ) : null}
                  {u.source_url ? (
                    <a href={u.source_url} target="_blank" rel="noreferrer noopener" className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium hover:underline">
                      <ExternalLink className="h-3.5 w-3.5" /> Source
                    </a>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {isOwner && user ? <AddUpdate projectId={id} userId={user.id} onDone={load} /> : null}
        </section>

        {/* Media & documents */}
        <section className="panel px-5 py-5">
          <h2 className="font-display text-[15px] font-semibold">Photos, video and documents</h2>
          <p className="text-xs text-muted-foreground">Site photos, video from the ground and the paperwork behind the work, published by the office.</p>
          {media.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">Nothing attached yet.</p>
          ) : (
            <>
              {media.some((m) => m.kind === "image") ? (
                <>
                  <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {media.filter((m) => m.kind === "image").map((m, i) => (
                      <figure key={m.id} className="relative overflow-hidden rounded-xl border border-border">
                        <button
                          type="button"
                          onClick={() => setPhotoIdx(i)}
                          aria-label={m.title ?? "Open project photo"}
                          className="block w-full"
                        >
                          <img src={optimizedImage(m.url, 700)} alt={m.title ?? "Project photo"} className="h-32 w-full object-cover" />
                        </button>
                        {m.title ? (
                          <figcaption className="truncate px-2.5 py-1.5 text-[11px] text-muted-foreground">{m.title}</figcaption>
                        ) : null}
                        {isOwner ? <RemoveMedia id={m.id} onDone={load} /> : null}
                      </figure>
                    ))}
                  </div>
                  <MediaLightbox
                    media={media.filter((m) => m.kind === "image").map((m) => ({
                      id: m.id, url: m.url, media_type: "image", position: m.position,
                    }))}
                    startIndex={photoIdx ?? 0}
                    open={photoIdx !== null}
                    onOpenChange={(o) => { if (!o) setPhotoIdx(null); }}
                  />
                </>
              ) : null}

              {media.some((m) => m.kind === "video") ? (
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {media.filter((m) => m.kind === "video").map((m) => {
                    const review = normalizeReviewState(m.review_state);
                    const watchable = canWatch(review, { isOwner, isReviewer });
                    return (
                    <figure key={m.id} className="relative overflow-hidden rounded-xl border border-border">
                      <div className="aspect-video w-full">
                        {watchable ? (
                          <VideoPlayer
                            source={{ id: m.id, url: m.url, posterUrl: m.poster_url, durationSeconds: m.duration_seconds, scope: "impact_media" }}
                            objectFit="contain"
                          />
                        ) : (
                          <MediaReviewPlaceholder state={review} />
                        )}
                      </div>
                      {review !== "approved" ? (
                        <div className="px-2.5 pt-2">
                          <MediaReviewBadge state={review} scope="impact_media" id={m.id} onDecided={load} />
                        </div>
                      ) : null}
                      {m.title ? (
                        <figcaption className="truncate px-2.5 py-1.5 text-[11px] text-muted-foreground">{m.title}</figcaption>
                      ) : null}
                      {isOwner ? <RemoveMedia id={m.id} onDone={load} /> : null}
                    </figure>
                    );
                  })}
                </div>
              ) : null}
              {media.some((m) => m.kind === "document" || m.kind === "audio") ? (
                <ul className="mt-3 space-y-2">
                  {media.filter((m) => m.kind === "document" || m.kind === "audio").map((m) => (
                    <li key={m.id} className="flex items-center gap-2 rounded-xl border border-border px-3.5 py-3">
                      <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <a
                        href={m.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="min-w-0 flex-1 truncate text-sm font-medium hover:underline"
                      >
                        {m.title ?? "Document"}
                      </a>
                      {isOwner ? (
                        <button
                          type="button"
                          aria-label="Remove document"
                          className="shrink-0 text-muted-foreground hover:text-foreground"
                          onClick={async () => { await removeProjectMedia(m.id); load(); }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : null}
            </>
          )}
          {isOwner && user ? <AddMedia projectId={id} userId={user.id} onDone={load} /> : null}
        </section>

        {/* Linked citizen reports */}
        <section className="panel px-5 py-5">
          <h2 className="font-display text-[15px] font-semibold">Reports behind this work</h2>
          <p className="text-xs text-muted-foreground">The original problems people documented on Xamseen.</p>
          {reports.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">No reports connected yet.</p>
          ) : (
            <ul className="mt-4 divide-y divide-border">
              {reports.map((r) => {
                const post = r.post;
                if (!post) return null;
                const statusMeta = STATUS_META[String(post.status)] ?? null;
                return (
                  <li key={r.post_id} className="flex items-start gap-3 py-3">
                    {post.image_url ? (
                      <img src={optimizedImage(post.image_url, 200)} alt="" className="h-14 w-14 shrink-0 rounded-lg object-cover" />
                    ) : null}
                    <div className="min-w-0 flex-1">
                      <Link to="/posts/$id" params={{ id: post.id }} className="line-clamp-2 text-sm font-semibold hover:underline">
                        {post.title || post.content?.slice(0, 90) || "Report"}
                      </Link>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                        {statusMeta ? <span style={{ color: statusMeta.color }} className="font-medium">{statusMeta.label}</span> : null}
                        {post.location_name ? <span>{post.location_name}</span> : null}
                        <span>{formatDate(post.created_at)}</span>
                      </div>
                      {r.note ? <p className="mt-1 text-xs italic text-muted-foreground">{r.note}</p> : null}
                    </div>
                    {isOwner ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => { await unlinkReport(id, post.id); await load(); }}
                        aria-label="Remove this report from the project"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
          {isOwner && user ? <LinkReports projectId={id} userId={user.id} city={project.city} onDone={load} /> : null}
        </section>

        {/* Community verification */}
        <section className="panel panel-accent px-5 py-5">
          <h2 className="font-display text-[15px] font-semibold">Did this actually happen?</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {project.status === "community_verified"
              ? "Enough residents confirmed the finished work for this record to be marked verified by the community."
              : "Once the office marks the work complete, three confirmations from residents mark it verified by the community."}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              variant={myStance === "confirmed" ? "default" : "outline"}
              size="sm"
              onClick={() => vote("confirmed")}
              disabled={isOwner}
            >
              <ThumbsUp className="mr-1.5 h-3.5 w-3.5" /> I have seen this done
            </Button>
            <Button
              variant={myStance === "disputed" ? "default" : "outline"}
              size="sm"
              onClick={() => vote("disputed")}
              disabled={isOwner}
            >
              <ThumbsDown className="mr-1.5 h-3.5 w-3.5" /> Not as described
            </Button>
            {metrics ? (
              <span className="text-xs text-muted-foreground">
                {metrics.confirmations} confirmed · {metrics.disputes} disputed
              </span>
            ) : null}
          </div>
          {isOwner ? (
            <p className="mt-2 text-[11px] text-muted-foreground">You cannot verify your own record.</p>
          ) : null}
        </section>

        {/* Citizen feedback */}
        <section className="panel px-5 py-5">
          <h2 className="font-display text-[15px] font-semibold">What people are saying</h2>
          {user ? <FeedbackForm projectId={id} userId={user.id} onDone={load} /> : (
            <p className="mt-2 text-sm text-muted-foreground">
              <Link to="/signin" className="font-medium text-foreground hover:underline">Sign in</Link> to leave feedback.
            </p>
          )}
          {feedback.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">No feedback yet.</p>
          ) : (
            <ul className="mt-4 space-y-4">
              {feedback.map((f) => (
                <li key={f.id} className="flex gap-3">
                  <Avatar className="h-9 w-9 shrink-0">
                    <AvatarImage src={f.author?.avatar_url ?? undefined} />
                    <AvatarFallback className="text-xs">{(f.author?.full_name || f.author?.username || "?")[0]}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">
                      {f.author?.full_name || f.author?.username || "Someone"}
                      <span className="ml-2 text-xs font-normal text-muted-foreground">{formatDate(f.created_at)}</span>
                    </p>
                    <p className="mt-0.5 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">{f.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </article>

      {isOwner && user ? (
        <ImpactProjectDialog
          open={editing}
          onOpenChange={setEditing}
          userId={user.id}
          project={project}
          onSaved={() => void load()}
        />
      ) : null}
    </AppShell>
  );
}

function Stat({ value, label }: { value: number; label: string }) {
  return (
    <div className="panel px-4 py-3.5">
      <p className="font-display text-2xl font-bold tabular-nums">{value.toLocaleString()}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

function SourceLink({ label, url, publisher }: { label: string; url: string; publisher?: string | null }) {
  return (
    <li>
      <a href={url} target="_blank" rel="noreferrer noopener" className="inline-flex items-start gap-1.5 hover:underline">
        <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span>
          {label}
          {publisher ? <span className="block text-xs text-muted-foreground">{publisher}</span> : null}
        </span>
      </a>
    </li>
  );
}

function TimelineSource({ url, label }: { url: string; label: string }) {
  let host = "";
  try { host = new URL(url).hostname.replace(/^www\./, ""); } catch { host = ""; }
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer noopener"
      className="mt-2 inline-flex max-w-full items-center gap-1.5 rounded-full border border-border bg-surface px-2.5 py-1 text-[11px] font-medium hover:bg-background"
    >
      <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
      <span className="truncate">{label}</span>
      {host ? <span className="shrink-0 text-muted-foreground">{host}</span> : null}
    </a>
  );
}

function RemoveMedia({ id, onDone }: { id: string; onDone: () => void }) {
  return (
    <button
      type="button"
      onClick={async () => { await removeProjectMedia(id); onDone(); }}
      className="absolute right-2 top-2 rounded-full bg-background/85 p-1.5 text-muted-foreground hover:text-foreground"
      aria-label="Remove attachment"
    >
      <Trash2 className="h-3.5 w-3.5" />
    </button>
  );
}

function AddUpdate({ projectId, userId, onDone }: { projectId: string; userId: string; onDone: () => void }) {
  const [body, setBody] = useState("");
  const [phase, setPhase] = useState<ImpactStatus>("in_progress");
  const [source, setSource] = useState("");
  const [busy, setBusy] = useState(false);

  return (
    <div className="mt-5 border-t border-border pt-4">
      <Label htmlFor="upd-body">Post an update</Label>
      <Textarea id="upd-body" rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="What changed since the last update?" className="mt-1.5" />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Select value={phase} onValueChange={(v) => setPhase(v as ImpactStatus)}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            {OFFICIAL_SETTABLE_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>{IMPACT_STATUS_META[s].label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input value={source} onChange={(e) => setSource(e.target.value)} placeholder="Source link (optional)" className="max-w-xs" />
        <Button
          size="sm"
          disabled={busy || !body.trim()}
          onClick={async () => {
            setBusy(true);
            try {
              await addProjectUpdate({ projectId, authorId: userId, body: body.trim(), phase, sourceUrl: source.trim() || null });
              setBody(""); setSource("");
              onDone();
            } catch (e: any) {
              toast.error(e?.message ?? "That update could not be posted.");
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Plus className="mr-1.5 h-3.5 w-3.5" />}
          Post update
        </Button>
      </div>
    </div>
  );
}

function AddMedia({ projectId, userId, onDone }: { projectId: string; userId: string; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const clips = useMediaUploads("impact");
  const [caption, setCaption] = useState("");
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  const [publisher, setPublisher] = useState("");

  async function upload(files: File[], kind: "image" | "document") {
    setBusy(true);
    let done = 0;
    try {
      for (const [i, file] of files.entries()) {
        const publicUrl = await uploadImpactFile(file, userId);
        const title = caption.trim()
          ? files.length > 1
            ? `${caption.trim()} (${i + 1})`
            : caption.trim()
          : file.name;
        await addProjectMedia(projectId, publicUrl, kind, title, i);
        done += 1;
      }
      setCaption("");
      toast.success(done === 1 ? "Attached." : `${done} files attached.`);
      onDone();
    } catch (e: any) {
      if (done > 0) onDone();
      toast.error(e?.message ?? "That file could not be attached.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-5 space-y-3 border-t border-border pt-4">
      <div>
        <Label htmlFor="media-caption">Caption for the next upload (optional)</Label>
        <Input
          id="media-caption"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder="Repaved section, Ring Road east"
          className="mt-1.5 max-w-md"
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <FileLabel busy={busy} accept="image/*" onPick={(f) => upload(f, "image")}>Add photos</FileLabel>
        <FileLabel busy={busy} accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt" onPick={(f) => upload(f, "document")}>
          Attach documents
        </FileLabel>
        <span className="text-xs text-muted-foreground">You can select several files at once.</span>
      </div>
      <div className="space-y-2 border-t border-border pt-3">
        <MediaAttachments items={clips.items} tasks={clips.tasks} onRemove={clips.remove} onMove={clips.move} />
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-border px-3.5 py-2 text-sm font-medium hover:bg-surface">
            {clips.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
            Add video
            <input
              type="file"
              accept={VIDEO_ACCEPT}
              multiple
              hidden
              disabled={clips.busy}
              onChange={(e) => { void clips.addFiles(Array.from(e.target.files ?? []), userId); e.target.value = ""; }}
            />
          </label>
          <Button
            size="sm"
            variant="outline"
            disabled={clips.items.length === 0 || clips.busy}
            onClick={async () => {
              try {
                for (const [i, m] of clips.items.entries()) {
                  await addProjectMedia(projectId, m.url, "video", caption.trim() || m.name, i, {
                    posterUrl: m.posterUrl,
                    durationSeconds: m.durationSeconds,
                    byteSize: m.byteSize,
                    storageBucket: m.storageBucket,
                    storagePath: m.storagePath,
                    posterPath: m.posterPath,
                  });
                }
                clips.clear();
                setCaption("");
                toast.success("Video published to this record.");
                onDone();
              } catch (e: any) {
                toast.error(e?.message ?? "That video could not be attached.");
              }
            }}
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Publish video
          </Button>
          <span className="text-xs text-muted-foreground">
            Up to {MEDIA_LIMITS.video.label} and {formatDuration(MEDIA_LIMITS.video.maxSeconds)} per clip.
          </span>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Source label" className="max-w-[190px]" />
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" className="max-w-xs" />
        <Input value={publisher} onChange={(e) => setPublisher(e.target.value)} placeholder="Publisher (optional)" className="max-w-[190px]" />
        <Button
          size="sm"
          variant="outline"
          disabled={!label.trim() || !url.trim()}
          onClick={async () => {
            try {
              await addProjectSource(projectId, label.trim(), url.trim(), publisher.trim() || null);
              setLabel(""); setUrl(""); setPublisher("");
              onDone();
            } catch (e: any) {
              toast.error(e?.message ?? "That source could not be added.");
            }
          }}
        >
          <Link2 className="mr-1.5 h-3.5 w-3.5" /> Add source
        </Button>
      </div>
    </div>
  );
}

function FileLabel({ busy, accept, onPick, children }: { busy: boolean; accept: string; onPick: (files: File[]) => void; children: React.ReactNode }) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-border px-3.5 py-2 text-sm font-medium hover:bg-surface">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
      {children}
      <input
        type="file"
        accept={accept}
        multiple
        className="hidden"
        disabled={busy}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length) onPick(files);
          e.target.value = "";
        }}
      />
    </label>
  );
}

function LinkReports({ projectId, userId, city, onDone }: { projectId: string; userId: string; city: string | null; onDone: () => void }) {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);

  async function run() {
    setSearching(true);
    try {
      setResults(await searchLinkableReports(term.trim(), city));
    } finally {
      setSearching(false);
    }
  }

  return (
    <div className="mt-5 border-t border-border pt-4">
      <Label htmlFor="link-search">Connect a report</Label>
      <div className="mt-1.5 flex gap-2">
        <Input id="link-search" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search reports by words or place" />
        <Button size="sm" variant="outline" onClick={run} disabled={searching}>
          {searching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
        </Button>
      </div>
      {results.length > 0 ? (
        <ul className="mt-3 divide-y divide-border">
          {results.map((r) => (
            <li key={r.id} className="flex items-center gap-3 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{r.title || r.content?.slice(0, 80)}</p>
                <p className="text-xs text-muted-foreground">{[r.location_name, formatDate(r.created_at)].filter(Boolean).join(" · ")}</p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={async () => {
                  try {
                    await linkReport(projectId, r.id, userId);
                    setResults((prev) => prev.filter((x) => x.id !== r.id));
                    onDone();
                  } catch (e: any) {
                    toast.error(e?.message ?? "That report could not be connected.");
                  }
                }}
              >
                <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Connect
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function FeedbackForm({ projectId, userId, onDone }: { projectId: string; userId: string; onDone: () => void }) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <div className="mt-3">
      <Textarea rows={2} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Share what you have seen on the ground." />
      <Button
        size="sm"
        className="mt-2"
        disabled={busy || !body.trim()}
        onClick={async () => {
          setBusy(true);
          try {
            await addProjectFeedback(projectId, userId, body.trim());
            setBody("");
            onDone();
          } catch (e: any) {
            toast.error(e?.message ?? "That feedback could not be posted.");
          } finally {
            setBusy(false);
          }
        }}
      >
        <MessageSquare className="mr-1.5 h-3.5 w-3.5" /> Post feedback
      </Button>
    </div>
  );
}
